import { StatsigClient, StatsigUser } from "@statsig/js-client"
import { AppEnv } from "../../lib/appEnv"
import { assertNever } from "../../lib/assertNever"
import { Setter, JSX } from "solid-js"
import { GatingProvider } from "./base"

type LiveProps = {
	sdkKey: string
	appEnv: AppEnv
	user: StatsigUser
	children: JSX.Element
}

export function LiveGatingProvider(props: LiveProps) {
	const tier = appEnvToGatingTier(props.appEnv)
	const factory = () => new StatsigClient(props.sdkKey, props.user, { environment: { tier } })
	const isReady = (client: StatsigClient) => client.loadingStatus === "Ready"
	const initialize = (client: StatsigClient, setIsLoading: Setter<boolean>) => {
		if (client.loadingStatus !== "Ready") {
			// Repeat calls to initializeAsync return the same promise.
			// But if the client is already loaded, we don't want the promise
			// resolution to trigger an extra render on `setIsLoading(false)`
			client
				.initializeAsync()
				.catch(console.error)
				.finally(() => setIsLoading(false))
		}
	}
	const subscribe = (client: StatsigClient, setIsLoading: Setter<boolean>) => {
		const onValuesUpdated = () => {
			setIsLoading(client.loadingStatus === "Ready")
		}

		client.on("values_updated", onValuesUpdated)

		return () => {
			client.flush().catch((err) => console.error("An error occurred during flush", err))

			client.off("values_updated", onValuesUpdated)
		}
	}

	return (
		<GatingProvider factory={factory} isReady={isReady} initialize={initialize} subscribe={subscribe}>
			{props.children}
		</GatingProvider>
	)
}

function appEnvToGatingTier(env: AppEnv): string {
	switch (env) {
		case "TEST":
		case "DEVELOPMENT":
			return "development"
		case "STAGING":
			return "staging"
		case "PRODUCTION":
			return "production"
		default:
			assertNever(env)
	}
}
